// LIFE — one world, two readings. The spinnable Mountain is painted by the
// home behind everything (orbit.tsx); here we only choose what sits on it:
// the Compass (Joel's ASTRA orbital compass, kept exactly as built) or the
// Mountain controls. Switching fades the compass in/out — the mountain never
// moves. A one-line NOW bar at the bottom returns to the Step.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { CompassCore, compassFixedCenterY } from '../olmps/CompassView';
import type { NowStep } from './useCurrentStep';
import type { Sector } from '../domain/olmps';
import type { OracleProject } from '../olmps/useOracleProjects';
import type { OracleSpine } from '../olmps/oracle-spine-data';
import type { Orbit } from './orbit';
import { MountainView } from './MountainView';
import { makeStyles, useV10 } from './theme';

export type LifeMode = 'compass' | 'mountain';

export function LifeView({
  userId,
  current,
  projects,
  spine,
  orbit,
  frameTopInRoot,
  frameSize,
  mode,
  onMode,
  onFocusStep,
  onOpenTool,
}: {
  userId: string | undefined;
  current: NowStep | null;
  projects: Record<Sector, OracleProject[]>;
  spine: OracleSpine | null;
  orbit: Orbit;
  /** Top of the orbit frame in root (window) coordinates; converted to ours. */
  frameTopInRoot: number;
  frameSize: number;
  mode: LifeMode;
  onMode: (m: LifeMode) => void;
  onFocusStep: () => void;
  onOpenTool: (tool: string) => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const { width } = useWindowDimensions();
  const bodyRef = useRef<View>(null);
  const [bodyY, setBodyY] = useState(0);
  // Keep the disc centred on the mountain's base (62% down the orbit frame).
  const frameTop = frameTopInRoot - bodyY;
  const compassTop = frameTop + frameSize * 0.62 - compassFixedCenterY(width);
  const measure = () => bodyRef.current?.measureInWindow((_x, y) => setBodyY(y));

  // Compass fades; the mountain behind stays put.
  const compassOpacity = useRef(new Animated.Value(mode === 'compass' ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(compassOpacity, { toValue: mode === 'compass' ? 1 : 0, duration: 420, useNativeDriver: true }).start();
  }, [mode, compassOpacity]);

  return (
    <View style={styles.root}>
      <View style={styles.modeRow}>
        {(['compass', 'mountain'] as LifeMode[]).map((m) => (
          <Pressable key={m} onPress={() => onMode(m)} style={[styles.modeBtn, mode === m && styles.modeBtnOn]} hitSlop={6}>
            <Text style={[styles.modeText, mode === m && { color: t.onGold }]}>{m === 'compass' ? 'Compass' : 'Mountain'}</Text>
          </Pressable>
        ))}
      </View>
      <View ref={bodyRef} onLayout={measure} style={{ flex: 1 }}>
        {mode === 'mountain' ? (
          <MountainView orbit={orbit} frameTop={frameTop} frameSize={frameSize} projects={projects} spine={spine} current={current} onOpenTool={onOpenTool} />
        ) : null}
        <Animated.View pointerEvents={mode === 'compass' ? 'auto' : 'none'} style={[StyleSheet.absoluteFill, { opacity: compassOpacity }]}>
          {mode === 'compass' ? (
            <View style={{ position: 'absolute', left: 0, right: 0, top: compassTop, bottom: 0 }}>
              <CompassCore userId={userId} onOpenTool={onOpenTool} compact={false} />
            </View>
          ) : null}
        </Animated.View>
      </View>
      {current ? (
        <Pressable onPress={onFocusStep} style={styles.nowBar}>
          <Text style={[styles.nowKicker, { color: t.gold }]}>NOW</Text>
          <Text style={styles.nowTitle} numberOfLines={1}>
            {current.title}
          </Text>
          <Text style={styles.nowChevron}>›</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1, paddingTop: 6 },
    modeRow: { flexDirection: 'row', alignSelf: 'flex-end', marginRight: 10, gap: 6, padding: 4, borderRadius: 999, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine, marginBottom: 8, height: 36 },
    modeBtn: { paddingHorizontal: 16, justifyContent: 'center', borderRadius: 999 },
    modeBtnOn: { backgroundColor: t.gold, boxShadow: `0 0 14px ${t.goldGlow}` } as object,
    modeText: { color: t.ivorySoft, fontSize: 12, fontWeight: '800', letterSpacing: 0.5 },
    nowBar: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 4, marginHorizontal: 10, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 999, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
    nowKicker: { fontSize: 10, fontWeight: '800', letterSpacing: 2 },
    nowTitle: { flex: 1, color: t.ivory, fontSize: 13.5, fontWeight: '700' },
    nowChevron: { color: t.goldBright, fontSize: 18, fontWeight: '700' },
  }),
);
